import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { getNextAvailableLiveMatches } from '../../../../../actions/event-profile-actions';

import styles from './nextAvailableLiveMatches.scss';
import { Input, Select } from '../../../../../components/Form';
import { MatchStatusTypes } from '../../../../../utils/nextAvailable';
import LiveMatch from './LiveMatch';

function NextAvailableLiveMatchesTabContent(props) {
    const { handleTabClick } = props;
    const dispatch = useDispatch();
    const { event, liveMatches, locations, draws, drawsTabState } = useSelector(({ eventProfile }) => {
        const { event, liveMatches, locations, draws, drawsTabState } = eventProfile;
        return { event, liveMatches, locations, draws, drawsTabState };
    });

    const matchStatusTypes = [
        {
            id: 1,
            statusTypesToDisplay: [
                MatchStatusTypes.IN_QUEUE,
                MatchStatusTypes.ON_COURT,
            ],
            label: 'All',
        },
        {
            id: 2,
            statusTypesToDisplay: [
                MatchStatusTypes.ON_COURT,
            ],
            label: 'On Court',
        },
        {
            id: 3,
            statusTypesToDisplay: [
                MatchStatusTypes.IN_QUEUE,
            ],
            label: 'Waiting',
        },
    ];

    const [filterParams, setFilterParams] = useState({
        facilityId: '',
        matchStatus: matchStatusTypes[0],
        searchText: '',
    });

    const onFacilitySelected = useCallback((selectedFacilityId) => {
        setFilterParams({
            ...filterParams,
            facilityId: selectedFacilityId,
        });
    }, [filterParams]);

    const displayableMatches = useMemo(() => {
        return liveMatches?.matches?.filter((match) => {
            if (filterParams.searchText) {
                const playerNames = [
                    ...match.position1Players,
                    ...match.position2Players,
                ].map((player) => {
                    return `${player.firstName} ${player.lastName}`;
                });

                return playerNames.find((playerName) => {
                    return playerName.toLowerCase().includes(filterParams.searchText.trim().toLowerCase());
                });
            }

            return true;
        }).filter((match) => {
            if (filterParams.facilityId) {
                return match.drawClubLocationFacilityId === filterParams.facilityId;
            }

            return true;
        });
    }, [liveMatches?.matches, filterParams.searchText, filterParams.facilityId]);

    const onSearchPlayers = (ev) => {
        setFilterParams({
            ...filterParams,
            searchText: ev.target.value,
        });
    };

    const onClickMatchStatusType = useCallback((statusType) => () => {
        if (statusType.id === filterParams.matchStatus.id) {
            return;
        }

        setFilterParams({
            ...filterParams,
            matchStatus: statusType,
            facilityId: '',
        });
    }, [filterParams]);

    const fetchLiveMatches = useCallback((showLoader = true) => {
        dispatch(getNextAvailableLiveMatches(
            event?.data?.id,
            null,
            filterParams.matchStatus.statusTypesToDisplay,
            showLoader,
            filterParams.matchStatus.id === 1,
        ));
    }, [filterParams.matchStatus, event?.data?.id]);

    useEffect(() => {
        const interval = setInterval(() => {
            fetchLiveMatches(false);
        }, 60000);

        return () => {
            clearInterval(interval);
        };
    }, [fetchLiveMatches]);

    useEffect(() => {
        if (!liveMatches.loading) {
            fetchLiveMatches(true);
        }
    }, [fetchLiveMatches]);

    useEffect(() => {
        if (liveMatches.facilitiesAvailable?.length === 1) {
            setFilterParams({
                ...filterParams,
                facilityId: liveMatches.facilitiesAvailable[0].id,
            });
        }
    }, [liveMatches.facilitiesAvailable]);

    return (
        <div className={`${styles.nextAvailableLiveMatches}`}>
            <div className={styles.filterContainer}>
                <Select
                    attributeToShow="name"
                    attributeToSave="id"
                    themeV3
                    emptyOption="All facilities"
                    options={liveMatches?.facilitiesAvailable}
                    formGroupCssClass={styles.filterSelect}
                    controlled
                    value={filterParams.facilityId}
                    onChange={onFacilitySelected}
                    disabled={liveMatches?.facilitiesAvailable?.length < 2}
                    data-testid="event-profile.live-matches.facility-select"
                />
                <div className={styles.typeSelector}>
                    {matchStatusTypes.map((statusType) => {
                        return (
                            <button
                                key={`status-type-selector-${statusType.id}`}
                                type="button"
                                className={`btn btn-sm flex-1 ${styles.typeSelectorButton} ${statusType.id === filterParams.matchStatus.id ? styles.selected : ''}`}
                                onClick={onClickMatchStatusType(statusType)}
                                data-testid={`event-profile.live-matches.status-type-${statusType.id}`}
                            >
                                {statusType.label}
                            </button>
                        );
                    })}
                </div>
                <Input
                    controlled
                    themeV3
                    formGroupCssClass={styles.searchInput}
                    placeholder="Find a player"
                    value={filterParams.searchText}
                    onChange={onSearchPlayers}
                    data-testid="event-profile.live-matches.search-input"
                />
            </div>
            <div className={styles.matchesContainer}>
                <div className={`${styles.header}`}>
                    <div className={styles.match} data-testid="event-profile.live-matches.header-match">
                        Match
                    </div>
                    <div className={styles.scoringFormat} data-testid="event-profile.live-matches.header-scoring-format">
                        Scoring format
                    </div>
                    {filterParams.matchStatus.id !== 3 && (
                        <div className={styles.court} data-testid="event-profile.live-matches.header-court">
                            Court
                        </div>
                    )}
                    <div className={styles.playerOrTeam} data-testid="event-profile.live-matches.header-position-1">
                        Player 1/ Team 1
                    </div>
                    <div className={styles.playerOrTeam} data-testid="event-profile.live-matches.header-position-2">
                        Player 2/ Team 2
                    </div>
                </div>
                {liveMatches?.showLoader && liveMatches?.loading && (
                    <div className={styles.spinner} data-testid="event-profile.live-matches.loading">
                        <div className={styles.iconWrapper}>
                            <i className="icon-utr-logo" />
                            <div className={styles.iconSpinner} />
                            <div className={`${styles.iconSpinner} ${styles.reverse}`} />
                        </div>
                        <div className={styles.title} data-testid="event-profile.live-matches.loading-text">Loading</div>
                    </div>
                )}
                {(!liveMatches?.showLoader || !liveMatches?.loading) && (
                    <div className={styles.content}>
                        {liveMatches?.matches?.length > 0 && displayableMatches.map((match) => {
                            return (
                                <LiveMatch
                                    key={`live-match-${match.id}`}
                                    match={match}
                                    locations={locations}
                                    eventSchedule={event?.data?.eventSchedule}
                                    utrTypeId={event?.data?.utrTypeId}
                                    sportTypeId={event?.data?.sportTypeId}
                                    filterParams={filterParams}
                                    handleTabClick={handleTabClick}
                                    eventId={event?.data?.id}
                                    drawsDict={draws?.drawsDict || {}}
                                    drawsTabState={drawsTabState}
                                />
                            );
                        })}
                        {liveMatches?.loadedAtLeastOnce && displayableMatches.length === 0 && (
                            <div className={styles.noMatches} data-testid="event-profile.live-matches.empty">
                                {`No results found.${liveMatches?.matches?.length > 0 ? ' Clear search and try again.' : ''}`}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default withRouter(NextAvailableLiveMatchesTabContent);
